import { createContext, useContext, type ReactNode } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { spotlight } from "@mantine/spotlight";
import { NAV_GROUPS } from "../nav";
import { useAuthStore, useIdentity } from "../stores/authStore";
import { useUiStore } from "../stores/uiStore";
import { useWorkspaceStore } from "../stores/workspaceStore";
import css from "./shell.module.css";

/**
 * Route prefix for the current mount: "" for the live console, "/dev" for the
 * static one. Every internal link goes through `useBase()` so the same screen
 * components serve both.
 */
const BaseContext = createContext<string>("");

export function BaseProvider({ base, children }: { base: string; children: ReactNode }) {
  return <BaseContext.Provider value={base}>{children}</BaseContext.Provider>;
}

export function useBase(): string {
  return useContext(BaseContext);
}

/** Prefixes an app-relative path with the active mount's base. */
export function useHref(): (to: string) => string {
  const base = useBase();
  return (to: string) => `${base}${to}`;
}

/** Navigate within the active mount. */
export function useGo(): (to: string) => void {
  const navigate = useNavigate();
  const base = useBase();
  return (to: string) => navigate(`${base}${to}`);
}

export function ConsoleShell({
  children,
  devBar,
}: {
  children: ReactNode;
  devBar?: ReactNode;
}) {
  const base = useBase();
  const scanlines = useUiStore((s) => s.scanlines);
  const openModal = useUiStore((s) => s.openModal);
  const identity = useIdentity();
  const signOut = useAuthStore((s) => s.signOutUser);
  const currentWs = useWorkspaceStore((s) => s.currentId);
  const navigate = useNavigate();

  return (
    <div className={css.root}>
      {scanlines ? <div className={css.scan} /> : null}

      <nav className={`${css.sidebar} vfsc`} aria-label="Console">
        <div className={css.brand}>
          <span className={css.mark} aria-hidden="true" />
          <span className={css.brandName}>Vector Forge</span>
        </div>

        <div className={css.navBody}>
          {NAV_GROUPS.map((group) => (
            <div key={group.group}>
              <div className={css.navGroup}>{group.group}</div>
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={`${base}${item.to}`}
                  className={({ isActive }) =>
                    isActive ? `${css.navItem} ${css.navItemActive}` : css.navItem
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span className={css.navBullet}>{isActive ? "▸" : "·"}</span>
                      {item.label}
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </div>

        <div className={css.userBar}>
          <span className={css.avatar}>{identity.initials}</span>
          <div className={css.userMeta}>
            <div className={css.userEmail}>{identity.email}</div>
            <div className={css.userRole}>{identity.role}</div>
          </div>
          <button
            type="button"
            className={css.exit}
            title="sign out"
            onClick={() => {
              void signOut().then(() => navigate(`${base}/signin`));
            }}
          >
            [exit]
          </button>
        </div>
      </nav>

      <div className={css.main}>
        {devBar}

        <div className={css.topbar}>
          <button type="button" className={css.wsSwitch} onClick={() => openModal("workspaceMenu")}>
            <span className={css.wsBadge}>{(currentWs ?? "?").charAt(0).toUpperCase()}</span>
            <span>{currentWs ?? "—"}</span>
            <span className={css.wsCaret}>▾</span>
          </button>

          <button type="button" className={css.commandBar} onClick={spotlight.open}>
            <span className={css.commandBarPrompt}>$</span>
            <span className={css.commandBarText}>search or run a command</span>
            <span className={css.kbd}>⌘K</span>
          </button>

          <span className={css.spacer} />
          <span className={css.operational}>● operational</span>
        </div>

        <main className={`${css.content} vfsc`}>{children}</main>
      </div>
    </div>
  );
}
